import React, { useState, useEffect, useMemo, useRef } from 'react';
import { AdminHeader } from './AdminLayout';
import { 
    getAngebote, 
    getInstrumente, 
    getUnterrichtsorte, 
    getLehrpersonen,
    getGemeinden,
    getRaeume,
    createAngebot, 
    updateAngebot, 
    deleteAngebot,
    getLehrerStatusOverview,
    getErrorMessage
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '@/components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Trash2, Calendar, Search, X, Clock, Music, MapPin, User, Building2, AlertCircle, CheckCircle2, ChevronDown, ChevronUp, DoorOpen, Check, FolderSync, FileDown, FileUp, Download, FileSpreadsheet, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const WOCHENTAGE = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

const chipStyles = {
    instrument: "bg-violet-50 text-violet-700 border-violet-200",
    lehrperson: "bg-sky-50 text-sky-700 border-sky-200",
    gemeinde: "bg-emerald-50 text-emerald-700 border-emerald-200",
    unterrichtsort: "bg-amber-50 text-amber-700 border-amber-200",
    ort: "bg-amber-50 text-amber-700 border-amber-200",
    wochentag: "bg-slate-100 text-slate-700 border-slate-200",
};

export function AngeboteList() {
    const [items, setItems] = useState([]);
    const [instrumente, setInstrumente] = useState([]);
    const [unterrichtsorte, setUnterrichtsorte] = useState([]);
    const [lehrpersonen, setLehrpersonen] = useState([]);
    const [gemeinden, setGemeinden] = useState([]);
    const [raeume, setRaeume] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Lehrer Status
    const [lehrerStatus, setLehrerStatus] = useState(null);
    const [showLehrerStatus, setShowLehrerStatus] = useState(false);
    
    // Import/Export Dialog
    const [dataDialogOpen, setDataDialogOpen] = useState(false);
    const [jsonImportFile, setJsonImportFile] = useState(null);
    const [jsonImporting, setJsonImporting] = useState(false);
    const [jsonImportResult, setJsonImportResult] = useState(null);
    const jsonInputRef = useRef(null);
    
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState({ type: null, id: null, name: null });
    
    const [editItem, setEditItem] = useState(null);
    const [deleteItem, setDeleteItem] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [formData, setFormData] = useState({
        instrument_ids: [], unterrichtsort_id: '', raum_id: '', lehrperson_id: '',
        wochentag: '', zeit_von: '', zeit_bis: '', bemerkung: '',
        gemeindeuebergreifend: false, spezialangebot_text: ''
    });

    const fetchData = async () => {
        try {
            setLoading(true);
            const [angeboteRes, instrumenteRes, unterrichtsorteRes, lehrpersonenRes, gemeindenRes, raeumeRes, statusRes] = await Promise.all([
                getAngebote(), getInstrumente(), getUnterrichtsorte(), getLehrpersonen(), getGemeinden(), getRaeume(),
                getLehrerStatusOverview().catch(() => ({ data: null }))
            ]);
            setItems(angeboteRes.data);
            setInstrumente(instrumenteRes.data.sort((a, b) => a.name.localeCompare(b.name)));
            setUnterrichtsorte(unterrichtsorteRes.data);
            setLehrpersonen(lehrpersonenRes.data.sort((a, b) => a.nachname.localeCompare(b.nachname)));
            setGemeinden(gemeindenRes.data.sort((a, b) => a.name.localeCompare(b.name)));
            setRaeume(raeumeRes.data);
            if (statusRes.data) setLehrerStatus(statusRes.data);
        } catch (error) {
            toast.error('Fehler beim Laden');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const searchResults = useMemo(() => {
        if (!searchTerm || searchTerm.length < 2) return { lehrpersonen: [], instrumente: [], gemeinden: [], orte: [], wochentage: [] };
        const term = searchTerm.toLowerCase();
        return {
            lehrpersonen: lehrpersonen.filter(l => `${l.vorname} ${l.nachname}`.toLowerCase().includes(term)).slice(0, 5),
            instrumente: instrumente.filter(i => i.name.toLowerCase().includes(term)).slice(0, 5),
            gemeinden: gemeinden.filter(g => g.name.toLowerCase().includes(term)).slice(0, 5),
            orte: unterrichtsorte.filter(o => o.name.toLowerCase().includes(term)).slice(0, 5),
            wochentage: WOCHENTAGE.filter(w => w.toLowerCase().includes(term)),
        };
    }, [searchTerm, lehrpersonen, instrumente, gemeinden, unterrichtsorte]);

    const hasSearchResults = searchResults.lehrpersonen.length > 0 || searchResults.instrumente.length > 0 || searchResults.gemeinden.length > 0 || searchResults.orte.length > 0 || searchResults.wochentage.length > 0;

    const filteredItems = useMemo(() => {
        if (!activeFilter.type) return items;
        switch (activeFilter.type) {
            case 'lehrperson': return items.filter(a => a.lehrperson_id === activeFilter.id);
            case 'instrument': return items.filter(a => {
                // Support both instrument_ids array and legacy instrument_id
                if (Array.isArray(a.instrument_ids) && a.instrument_ids.includes(activeFilter.id)) return true;
                if (a.instrument_id === activeFilter.id) return true;
                return false;
            });
            case 'gemeinde': return items.filter(a => a.gemeinde_id === activeFilter.id);
            case 'ort': return items.filter(a => a.unterrichtsort_id === activeFilter.id);
            case 'wochentag': return items.filter(a => a.wochentag === activeFilter.id);
            default: return items;
        }
    }, [items, activeFilter]);

    const groupedByDay = useMemo(() => {
        const byDay = {};
        filteredItems.forEach(a => {
            if (!byDay[a.wochentag]) byDay[a.wochentag] = [];
            byDay[a.wochentag].push(a);
        });
        Object.keys(byDay).forEach(day => byDay[day].sort((a, b) => a.zeit_von.localeCompare(b.zeit_von)));
        return byDay;
    }, [filteredItems]);

    const setFilter = (type, id, name) => { setActiveFilter({ type, id, name }); setSearchTerm(''); };
    const clearFilter = () => { setActiveFilter({ type: null, id: null, name: null }); };

    const handleOpenDialog = (item = null) => {
        if (item) {
            setEditItem(item);
            // Support both instrument_ids array and legacy instrument_id
            const instrumentIds = item.instrument_ids?.length > 0 
                ? item.instrument_ids 
                : (item.instrument_id ? [item.instrument_id] : []);
            setFormData({
                instrument_ids: instrumentIds, 
                unterrichtsort_id: item.unterrichtsort_id,
                raum_id: item.raum_id || '',
                lehrperson_id: item.lehrperson_id, wochentag: item.wochentag,
                zeit_von: item.zeit_von, zeit_bis: item.zeit_bis, bemerkung: item.bemerkung || '',
                gemeindeuebergreifend: item.gemeindeuebergreifend || false,
                spezialangebot_text: item.spezialangebot_text || ''
            });
        } else {
            setEditItem(null);
            setFormData({ instrument_ids: [], unterrichtsort_id: '', raum_id: '', lehrperson_id: '', wochentag: '', zeit_von: '', zeit_bis: '', bemerkung: '', gemeindeuebergreifend: false, spezialangebot_text: '' });
        }
        setDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            if (editItem) {
                await updateAngebot(editItem.id, formData);
                toast.success('Angebot aktualisiert');
            } else {
                await createAngebot(formData);
                toast.success('Angebot erstellt');
            }
            setDialogOpen(false);
            fetchData();
        } catch (error) {
            toast.error(getErrorMessage(error, 'Fehler beim Speichern'));
        }
    };

    const handleDelete = async () => {
        try {
            await deleteAngebot(deleteItem.id);
            toast.success('Angebot gelöscht');
            setDeleteItem(null);
            fetchData();
        } catch (error) {
            toast.error(getErrorMessage(error, 'Fehler beim Löschen'));
        }
    };

    // === IMPORT/EXPORT FUNKTIONEN ===
    const handleJSONExport = () => {
        const exportData = items.map(item => ({
            id: item.id,
            instrument_id: item.instrument_id,
            instrument_ids: item.instrument_ids || [],
            lehrperson_id: item.lehrperson_id,
            unterrichtsort_id: item.unterrichtsort_id,
            raum_id: item.raum_id,
            wochentag: item.wochentag,
            zeit_von: item.zeit_von,
            zeit_bis: item.zeit_bis,
            bemerkung: item.bemerkung,
            gemeindeuebergreifend: item.gemeindeuebergreifend,
            spezialangebot_text: item.spezialangebot_text
        }));
        
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `angebote_backup_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`${exportData.length} Angebote exportiert`);
    };

    const handleCSVExport = () => {
        const headers = ['Instrument', 'Lehrperson', 'Gemeinde', 'Unterrichtsort', 'Raum', 'Wochentag', 'Von', 'Bis', 'Bemerkung'];
        const rows = items.map(item => [
            item.instrument_name || '',
            item.lehrperson_name || '',
            item.gemeinde_name || '',
            item.unterrichtsort_name || '',
            item.raum_name || '',
            item.wochentag || '',
            item.zeit_von || '',
            item.zeit_bis || '',
            item.bemerkung || ''
        ]);
        
        const csvContent = [headers, ...rows]
            .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
            .join('\n');
        
        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `angebote_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`${items.length} Angebote als CSV exportiert`);
    };

    const handleJSONImport = async () => {
        if (!jsonImportFile) return;
        
        try {
            setJsonImporting(true);
            setJsonImportResult(null);
            
            const text = await jsonImportFile.text();
            const data = JSON.parse(text);
            
            if (!Array.isArray(data)) {
                throw new Error('JSON muss ein Array sein');
            }
            
            let imported = 0;
            let updated = 0;
            const errors = [];
            
            for (const item of data) {
                try {
                    // Check if exists
                    const existing = items.find(i => i.id === item.id);
                    if (existing) {
                        await updateAngebot(item.id, item);
                        updated++;
                    } else {
                        await createAngebot(item);
                        imported++;
                    }
                } catch (err) {
                    errors.push({ id: item.id, error: err.message });
                }
            }
            
            setJsonImportResult({ imported, updated, errors });
            
            if (imported > 0 || updated > 0) {
                toast.success(`${imported} neu, ${updated} aktualisiert`);
                fetchData();
            }
            
            // Reset
            setJsonImportFile(null);
            if (jsonInputRef.current) jsonInputRef.current.value = '';
            
        } catch (err) {
            toast.error('Import fehlgeschlagen: ' + err.message);
            setJsonImportResult({ imported: 0, updated: 0, errors: [{ error: err.message }] });
        } finally {
            setJsonImporting(false);
        }
    };

    const isFormValid = formData.instrument_ids?.length > 0 && formData.unterrichtsort_id && formData.raum_id && formData.lehrperson_id && formData.wochentag && formData.zeit_von && formData.zeit_bis;
    
    // State for expanded sections
    const [showAllInstruments, setShowAllInstruments] = useState(false);
    const [showAllGemeinden, setShowAllGemeinden] = useState(false);
    
    // Selected Gemeinde for filtering
    const [selectedGemeinde, setSelectedGemeinde] = useState('');
    
    // Toggle instrument selection (multi-select)
    const toggleInstrument = (instId) => {
        const current = formData.instrument_ids || [];
        if (current.includes(instId)) {
            // Allow deselecting but keep at least one
            if (current.length > 1) {
                setFormData({ ...formData, instrument_ids: current.filter(id => id !== instId) });
            }
        } else {
            setFormData({ ...formData, instrument_ids: [...current, instId] });
        }
    };
    
    // Get selected Lehrperson data
    const selectedLehrer = lehrpersonen.find(l => l.id === formData.lehrperson_id);
    
    // Filter Gemeinden based on selected Lehrer
    const filteredGemeinden = selectedLehrer?.gemeinden?.length > 0
        ? gemeinden.filter(g => selectedLehrer.gemeinden.includes(g.id))
        : [];
    
    // Filter Unterrichtsorte based on selected Gemeinde
    const filteredOrte = selectedGemeinde
        ? unterrichtsorte.filter(o => o.gemeinde_id === selectedGemeinde)
        : [];
    
    // Filter räume by selected unterrichtsort
    const filteredRaeume = formData.unterrichtsort_id 
        ? raeume.filter(r => r.unterrichtsort_id === formData.unterrichtsort_id)
        : [];
    
    // Get raum name helper
    const getRaumName = (raumId) => {
        const raum = raeume.find(r => r.id === raumId);
        return raum ? (raum.anzeigename || raum.bezeichnung) : '';
    };
    
    // Get Gemeinde name helper
    const getGemeindeName = (gemeindeId) => {
        const g = gemeinden.find(g => g.id === gemeindeId);
        return g?.name || '';
    };
    
    // Handle Lehrer change - reset dependent fields
    const handleLehrerChange = (lehrpersonId) => {
        setFormData({
            ...formData,
            lehrperson_id: lehrpersonId,
            instrument_ids: [],
            unterrichtsort_id: '',
            raum_id: ''
        });
        setSelectedGemeinde('');
        setShowAllInstruments(false);
    };
    
    // Handle Gemeinde change
    const handleGemeindeChange = (gemeindeId) => {
        setSelectedGemeinde(gemeindeId);
        setFormData({ ...formData, unterrichtsort_id: '', raum_id: '' });
    };
    
    // Handle Dialog open - also set selectedGemeinde from existing data
    const handleOpenDialogEnhanced = (item = null) => {
        if (item) {
            setEditItem(item);
            const instrumentIds = item.instrument_ids?.length > 0 
                ? item.instrument_ids 
                : (item.instrument_id ? [item.instrument_id] : []);
            setFormData({
                instrument_ids: instrumentIds, 
                unterrichtsort_id: item.unterrichtsort_id,
                raum_id: item.raum_id || '',
                lehrperson_id: item.lehrperson_id, 
                wochentag: item.wochentag,
                zeit_von: item.zeit_von, 
                zeit_bis: item.zeit_bis, 
                bemerkung: item.bemerkung || '',
                gemeindeuebergreifend: item.gemeindeuebergreifend || false,
                spezialangebot_text: item.spezialangebot_text || ''
            });
            // Set gemeinde from unterrichtsort
            const ort = unterrichtsorte.find(o => o.id === item.unterrichtsort_id);
            setSelectedGemeinde(ort?.gemeinde_id || '');
        } else {
            setEditItem(null);
            setFormData({ 
                instrument_ids: [], 
                unterrichtsort_id: '', 
                raum_id: '', 
                lehrperson_id: '', 
                wochentag: '', 
                zeit_von: '', 
                zeit_bis: '', 
                bemerkung: '', 
                gemeindeuebergreifend: false, 
                spezialangebot_text: '' 
            });
            setSelectedGemeinde('');
        }
        setShowAllInstruments(false);
        setShowAllGemeinden(false);
        setDialogOpen(true);
    };

    return (
        <>
            <AdminHeader title="Angebote">
                <div className="flex items-center gap-2">
                    <Button 
                        variant="outline" 
                        onClick={() => setDataDialogOpen(true)} 
                        className="rounded-xl"
                        data-testid="angebote-data-btn"
                    >
                        <FolderSync className="h-4 w-4 mr-2" />
                        Import / Export
                    </Button>
                    <Button onClick={() => handleOpenDialogEnhanced()} className="rounded-xl">
                        <Plus className="h-4 w-4 mr-2" />
                        Neues Angebot
                    </Button>
                </div>
            </AdminHeader>

            <div className="admin-content">
                {/* Lehrer Status Banner */}
                {lehrerStatus && lehrerStatus.missing.length > 0 && (
                    <div className="mb-6">
                        <button
                            onClick={() => setShowLehrerStatus(!showLehrerStatus)}
                            className="w-full p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between hover:bg-amber-100 transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <AlertCircle className="h-5 w-5 text-amber-600" />
                                <span className="font-medium text-amber-800">
                                    {lehrerStatus.missing.length} Lehrer {lehrerStatus.missing.length === 1 ? 'hat' : 'haben'} noch keine Angebote eingetragen
                                </span>
                            </div>
                            {showLehrerStatus ? (
                                <ChevronUp className="h-5 w-5 text-amber-600" />
                            ) : (
                                <ChevronDown className="h-5 w-5 text-amber-600" />
                            )}
                        </button>
                        
                        {showLehrerStatus && (
                            <div className="mt-2 bg-white border rounded-xl p-4 space-y-4 animate-in fade-in duration-200">
                                {/* Missing */}
                                <div>
                                    <h4 className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-2">
                                        <AlertCircle className="h-4 w-4" />
                                        Fehlend ({lehrerStatus.missing.length})
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                        {lehrerStatus.missing.map(l => (
                                            <span
                                                key={l.id}
                                                className="px-3 py-1.5 bg-amber-50 text-amber-800 border border-amber-200 rounded-full text-sm font-medium"
                                            >
                                                {l.vorname} {l.nachname}
                                                {l.instrument_names.length > 0 && (
                                                    <span className="text-amber-600 ml-1 text-xs">
                                                        ({l.instrument_names.slice(0, 2).join(', ')}{l.instrument_names.length > 2 ? '...' : ''})
                                                    </span>
                                                )}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Submitted */}
                                <div>
                                    <h4 className="text-sm font-semibold text-blue-700 mb-2 flex items-center gap-2">
                                        <CheckCircle2 className="h-4 w-4" />
                                        Eingetragen ({lehrerStatus.submitted.length})
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                        {lehrerStatus.submitted.map(l => (
                                            <button
                                                key={l.id}
                                                onClick={() => { setFilter('lehrperson', l.id, `${l.vorname} ${l.nachname}`); setShowLehrerStatus(false); }}
                                                className="px-3 py-1.5 bg-blue-50 text-blue-800 border border-blue-200 rounded-full text-sm font-medium hover:bg-blue-100 transition-colors"
                                            >
                                                {l.vorname} {l.nachname}
                                                <span className="text-blue-600 ml-1 text-xs">
                                                    ({l.angebote_count} {l.angebote_count === 1 ? 'Angebot' : 'Angebote'})
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                
                {/* All teachers submitted banner */}
                {lehrerStatus && lehrerStatus.missing.length === 0 && lehrerStatus.submitted.length > 0 && (
                    <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3">
                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                        <span className="font-medium text-emerald-800">
                            Alle {lehrerStatus.submitted.length} Lehrer haben ihre Angebote eingetragen!
                        </span>
                    </div>
                )}
                
                {/* Search & Filter */}
                <div className="mb-8">
                    <div className="flex flex-wrap items-center gap-4 mb-4">
                        <div className="search-wrapper flex-1 max-w-xl">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 transition-colors" />
                            <Input
                                placeholder="Suchen nach Lehrer, Instrument, Gemeinde..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                            
                            {hasSearchResults && (
                                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border z-50 overflow-hidden">
                                    {searchResults.instrumente.length > 0 && (
                                        <div className="p-2">
                                            <p className="text-xs font-semibold text-muted-foreground px-2 py-1">Instrumente</p>
                                            {searchResults.instrumente.map(i => (
                                                <button key={i.id} onClick={() => setFilter('instrument', i.id, i.name)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-violet-50 flex items-center gap-2">
                                                    <Music className="h-4 w-4 text-violet-600" /><span>{i.name}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {searchResults.lehrpersonen.length > 0 && (
                                        <div className="p-2 border-t">
                                            <p className="text-xs font-semibold text-muted-foreground px-2 py-1">Lehrpersonen</p>
                                            {searchResults.lehrpersonen.map(l => (
                                                <button key={l.id} onClick={() => setFilter('lehrperson', l.id, `${l.vorname} ${l.nachname}`)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-sky-50 flex items-center gap-2">
                                                    <User className="h-4 w-4 text-sky-600" /><span>{l.vorname} {l.nachname}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {searchResults.gemeinden.length > 0 && (
                                        <div className="p-2 border-t">
                                            <p className="text-xs font-semibold text-muted-foreground px-2 py-1">Gemeinden</p>
                                            {searchResults.gemeinden.map(g => (
                                                <button key={g.id} onClick={() => setFilter('gemeinde', g.id, g.name)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-emerald-50 flex items-center gap-2">
                                                    <Building2 className="h-4 w-4 text-emerald-600" /><span>{g.name}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {searchResults.orte.length > 0 && (
                                        <div className="p-2 border-t">
                                            <p className="text-xs font-semibold text-muted-foreground px-2 py-1">Unterrichtsorte</p>
                                            {searchResults.orte.map(o => (
                                                <button key={o.id} onClick={() => setFilter('ort', o.id, `${o.name} (${o.gemeinde_name})`)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-amber-50 flex items-center gap-2">
                                                    <MapPin className="h-4 w-4 text-amber-600" /><span>{o.name}</span><span className="text-muted-foreground text-sm">({o.gemeinde_name})</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {searchResults.wochentage.length > 0 && (
                                        <div className="p-2 border-t">
                                            <p className="text-xs font-semibold text-muted-foreground px-2 py-1">Wochentage</p>
                                            {searchResults.wochentage.map(w => (
                                                <button key={w} onClick={() => setFilter('wochentag', w, w)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-100 flex items-center gap-2">
                                                    <Calendar className="h-4 w-4 text-slate-600" /><span>{w}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="text-base text-muted-foreground">{filteredItems.length} von {items.length} Angeboten</div>
                    </div>
                    
                    {activeFilter.type && (
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">Filter:</span>
                            <button onClick={clearFilter} className={cn("inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-all", chipStyles[activeFilter.type])}>
                                {activeFilter.type === 'instrument' && <Music className="h-4 w-4" />}
                                {activeFilter.type === 'lehrperson' && <User className="h-4 w-4" />}
                                {activeFilter.type === 'gemeinde' && <Building2 className="h-4 w-4" />}
                                {activeFilter.type === 'ort' && <MapPin className="h-4 w-4" />}
                                {activeFilter.type === 'wochentag' && <Calendar className="h-4 w-4" />}
                                {activeFilter.name}
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    )}
                </div>

                {/* Results - Spalten: Zeit | Person | Instrument | Ort */}
                {loading ? (
                    <div className="space-y-4">
                        {[1,2,3].map(i => (<div key={i} className="bg-card rounded-2xl border p-6 animate-pulse"><div className="h-6 bg-muted rounded w-32 mb-4" /><div className="h-12 bg-muted rounded" /></div>))}
                    </div>
                ) : filteredItems.length === 0 ? (
                    <div className="empty-state py-16">
                        <Calendar className="empty-state-icon" />
                        <p className="empty-state-title">Keine Angebote gefunden</p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {WOCHENTAGE.map(tag => {
                            const tagItems = groupedByDay[tag];
                            if (!tagItems || tagItems.length === 0) return null;
                            return (
                                <div key={tag}>
                                    <h3 className="text-lg font-bold text-foreground mb-3 flex items-center gap-3">
                                        <span className="px-3 py-1 rounded-lg text-sm font-semibold bg-slate-100 text-slate-600">{tag}</span>
                                        <span className="text-muted-foreground font-normal text-base">{tagItems.length} Angebote</span>
                                    </h3>
                                    <div className="data-card">
                                        <table className="admin-table">
                                            <thead>
                                                <tr>
                                                    <th className="w-28">Zeit</th>
                                                    <th>Person</th>
                                                    <th>Instrument</th>
                                                    <th>Ort</th>
                                                    <th className="w-12"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {tagItems.map(item => (
                                                    <tr key={item.id} onClick={() => handleOpenDialogEnhanced(item)} className="cursor-pointer">
                                                        <td>
                                                            <span className="inline-flex items-center gap-1.5 font-semibold text-foreground whitespace-nowrap">
                                                                <Clock className="h-4 w-4 text-slate-400" />{item.zeit_von}–{item.zeit_bis}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <button onClick={(e) => { e.stopPropagation(); setFilter('lehrperson', item.lehrperson_id, item.lehrperson_name); }} className={cn("chip chip-sm hover:ring-2 hover:ring-offset-1", chipStyles.lehrperson)}>
                                                                {item.lehrperson_name}
                                                            </button>
                                                        </td>
                                                        <td>
                                                            <button onClick={(e) => { 
                                                                e.stopPropagation(); 
                                                                const firstId = item.instrument_ids?.[0] || item.instrument_id;
                                                                setFilter('instrument', firstId, item.instrument_name); 
                                                            }} className={cn("chip chip-sm hover:ring-2 hover:ring-offset-1", chipStyles.instrument)}>
                                                                {item.instrument_name}
                                                            </button>
                                                        </td>
                                                        <td>
                                                            <button onClick={(e) => { e.stopPropagation(); setFilter('gemeinde', item.gemeinde_id, item.gemeinde_name); }} className={cn("chip chip-sm hover:ring-2 hover:ring-offset-1 mr-2", chipStyles.gemeinde)}>
                                                                {item.gemeinde_name}
                                                            </button>
                                                            <button onClick={(e) => { e.stopPropagation(); setFilter('ort', item.unterrichtsort_id, `${item.unterrichtsort_name} (${item.gemeinde_name})`); }} className={cn("chip chip-sm hover:ring-2 hover:ring-offset-1", chipStyles.ort)}>
                                                                {item.unterrichtsort_name}
                                                            </button>
                                                            {item.raum_id && (
                                                                <span className="ml-2 text-xs text-gray-500">({getRaumName(item.raum_id)})</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <button onClick={(e) => { e.stopPropagation(); setDeleteItem(item); }} className="btn-icon btn-icon-destructive">
                                                                <Trash2 className="h-4 w-4" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Step-Based Dialog like LehrerPortal */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-3xl max-h-[90vh]">
                    <DialogHeader>
                        <DialogTitle>{editItem ? 'Angebot bearbeiten' : 'Neues Angebot'}</DialogTitle>
                        <DialogDescription>
                            {editItem ? 'Ändern Sie die Details dieses Angebots.' : 'Erstellen Sie ein neues Unterrichtsangebot.'}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="py-4 space-y-6 max-h-[70vh] overflow-y-auto pr-2">
                        {/* Step 1: Lehrperson */}
                        <div>
                            <Label className="text-base font-semibold mb-3 flex items-center gap-2">
                                <span className={cn(
                                    "w-6 h-6 rounded-full inline-flex items-center justify-center text-sm",
                                    formData.lehrperson_id ? "bg-sky-600 text-white" : "bg-sky-500 text-white"
                                )}>1</span>
                                Lehrperson {!formData.lehrperson_id && "👈"}
                            </Label>
                            <Select value={formData.lehrperson_id} onValueChange={handleLehrerChange}>
                                <SelectTrigger className="w-full"><SelectValue placeholder="Lehrperson wählen..." /></SelectTrigger>
                                <SelectContent className="max-h-[300px]">
                                    {lehrpersonen.map(l => (
                                        <SelectItem key={l.id} value={l.id}>
                                            <span className="flex items-center gap-2">
                                                <User className="w-4 h-4" />
                                                {l.nachname} {l.vorname}
                                                {l.instrumente_names?.length > 0 && (
                                                    <span className="text-xs text-muted-foreground">
                                                        ({l.instrumente_names.slice(0, 2).join(', ')}{l.instrumente_names.length > 2 ? '...' : ''})
                                                    </span>
                                                )}
                                            </span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {selectedLehrer && (
                                <p className="text-xs text-muted-foreground mt-2">
                                    {selectedLehrer.instrumente_names?.length || 0} Instrumente • {selectedLehrer.gemeinden?.length || 0} Gemeinden
                                </p>
                            )}
                        </div>
                        
                        {/* Step 2: Gemeinde */}
                        <div className={cn(!formData.lehrperson_id && "opacity-40 pointer-events-none")}>
                            <Label className="text-base font-semibold mb-3 flex items-center gap-2">
                                <span className={cn(
                                    "w-6 h-6 rounded-full inline-flex items-center justify-center text-sm",
                                    selectedGemeinde ? "bg-emerald-600 text-white" : "bg-emerald-500 text-white"
                                )}>2</span>
                                Gemeinde {formData.lehrperson_id && !selectedGemeinde && "👈"}
                            </Label>
                            
                            {filteredGemeinden.length > 0 ? (
                                <>
                                    <div className="flex flex-wrap gap-2">
                                        {filteredGemeinden.map(g => (
                                            <button
                                                key={g.id}
                                                type="button"
                                                onClick={() => handleGemeindeChange(g.id)}
                                                className={cn(
                                                    "px-4 py-2.5 rounded-xl text-sm font-medium border-2 transition-all",
                                                    selectedGemeinde === g.id
                                                        ? "bg-emerald-600 border-emerald-600 text-white"
                                                        : "bg-emerald-50 border-emerald-200 text-emerald-800 hover:border-emerald-400 hover:bg-emerald-100"
                                                )}
                                            >
                                                {selectedGemeinde === g.id && <Check className="w-4 h-4 inline mr-1" />}
                                                {g.name}
                                            </button>
                                        ))}
                                        {/* Alle Gemeinden Button */}
                                        <button
                                            type="button"
                                            onClick={() => setShowAllGemeinden(!showAllGemeinden)}
                                            className="px-4 py-2.5 rounded-xl text-sm font-medium border-2 border-dashed border-emerald-300 text-emerald-500 hover:border-emerald-400 hover:bg-emerald-50 transition-all flex items-center gap-1.5"
                                        >
                                            <Plus className="w-4 h-4" />
                                            Andere Gemeinde
                                        </button>
                                    </div>
                                    
                                    {/* All Gemeinden expanded */}
                                    {showAllGemeinden && (
                                        <div className="mt-3 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                                            <p className="text-sm font-medium text-emerald-700 mb-3">Alle Gemeinden:</p>
                                            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                                                {gemeinden.filter(g => !filteredGemeinden.find(fg => fg.id === g.id)).map(g => (
                                                    <button
                                                        key={g.id}
                                                        type="button"
                                                        onClick={() => { handleGemeindeChange(g.id); setShowAllGemeinden(false); }}
                                                        className="px-3 py-2 rounded-lg text-sm border transition-all bg-white border-emerald-200 text-emerald-800 hover:border-emerald-400"
                                                    >
                                                        {g.name}
                                                    </button>
                                                ))}
                                            </div>
                                            <button 
                                                type="button"
                                                onClick={() => setShowAllGemeinden(false)} 
                                                className="text-sm text-emerald-600 hover:text-emerald-800 flex items-center gap-1 mt-2"
                                            >
                                                <ChevronUp className="w-4 h-4" /> Schließen
                                            </button>
                                        </div>
                                    )}
                                </>
                            ) : formData.lehrperson_id ? (
                                <div className="space-y-2">
                                    <p className="text-sm text-muted-foreground italic">Keine Gemeinden für diesen Lehrer hinterlegt</p>
                                    <div className="flex flex-wrap gap-2">
                                        {gemeinden.map(g => (
                                            <button
                                                key={g.id}
                                                type="button"
                                                onClick={() => handleGemeindeChange(g.id)}
                                                className={cn(
                                                    "px-3 py-2 rounded-lg text-sm border transition-all",
                                                    selectedGemeinde === g.id
                                                        ? "bg-emerald-600 border-emerald-600 text-white"
                                                        : "bg-white border-emerald-200 text-emerald-800 hover:border-emerald-400"
                                                )}
                                            >
                                                {g.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                        
                        {/* Step 3: Unterrichtsort */}
                        <div className={cn(!selectedGemeinde && "opacity-40 pointer-events-none")}>
                            <Label className="text-base font-semibold mb-3 flex items-center gap-2">
                                <span className={cn(
                                    "w-6 h-6 rounded-full inline-flex items-center justify-center text-sm",
                                    formData.unterrichtsort_id ? "bg-amber-600 text-white" : "bg-amber-500 text-white"
                                )}>3</span>
                                Unterrichtsort in {getGemeindeName(selectedGemeinde) || '...'} {selectedGemeinde && !formData.unterrichtsort_id && "👈"}
                            </Label>
                            
                            {filteredOrte.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                    {filteredOrte.map(ort => (
                                        <button
                                            key={ort.id}
                                            type="button"
                                            onClick={() => setFormData({ ...formData, unterrichtsort_id: ort.id, raum_id: '' })}
                                            className={cn(
                                                "px-4 py-2.5 rounded-xl text-sm font-medium border-2 transition-all",
                                                formData.unterrichtsort_id === ort.id
                                                    ? "bg-amber-600 border-amber-600 text-white"
                                                    : "bg-amber-50 border-amber-200 text-amber-800 hover:border-amber-400 hover:bg-amber-100"
                                            )}
                                        >
                                            {formData.unterrichtsort_id === ort.id && <Check className="w-4 h-4 inline mr-1" />}
                                            <MapPin className="w-4 h-4 inline mr-1" />
                                            {ort.name}
                                        </button>
                                    ))}
                                </div>
                            ) : selectedGemeinde ? (
                                <p className="text-sm text-muted-foreground italic">Keine Unterrichtsorte in dieser Gemeinde</p>
                            ) : null}
                        </div>
                        
                        {/* Step 4: Raum */}
                        <div className={cn(!formData.unterrichtsort_id && "opacity-40 pointer-events-none")}>
                            <Label className="text-base font-semibold mb-3 flex items-center gap-2">
                                <span className={cn(
                                    "w-6 h-6 rounded-full inline-flex items-center justify-center text-sm",
                                    formData.raum_id ? "bg-orange-600 text-white" : "bg-orange-500 text-white"
                                )}>4</span>
                                Raum {formData.unterrichtsort_id && !formData.raum_id && "👈"}
                            </Label>
                            {filteredRaeume.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                    {filteredRaeume.map(raum => (
                                        <button
                                            key={raum.id}
                                            type="button"
                                            onClick={() => setFormData({ ...formData, raum_id: raum.id })}
                                            className={cn(
                                                "px-4 py-2.5 rounded-xl text-sm font-medium border-2 transition-all",
                                                formData.raum_id === raum.id
                                                    ? "bg-orange-600 border-orange-600 text-white"
                                                    : "bg-orange-50 border-orange-200 text-orange-800 hover:border-orange-400 hover:bg-orange-100"
                                            )}
                                        >
                                            {formData.raum_id === raum.id && <Check className="w-4 h-4 inline mr-1" />}
                                            <DoorOpen className="w-4 h-4 inline mr-1" />
                                            {raum.anzeigename || raum.bezeichnung}
                                            {raum.ist_standard && <span className="text-xs opacity-70 ml-1">(Standard)</span>}
                                        </button>
                                    ))}
                                </div>
                            ) : formData.unterrichtsort_id ? (
                                <p className="text-sm text-muted-foreground italic">Keine Räume für diesen Unterrichtsort</p>
                            ) : null}
                        </div>
                        
                        {/* Step 5: Instrumente (aus Lehrer-Daten + hinzufügen) */}
                        <div className={cn(!formData.raum_id && "opacity-40 pointer-events-none")}>
                            <Label className="text-base font-semibold mb-3 flex items-center gap-2">
                                <span className={cn(
                                    "w-6 h-6 rounded-full inline-flex items-center justify-center text-sm",
                                    formData.instrument_ids?.length > 0 ? "bg-violet-600 text-white" : "bg-violet-500 text-white"
                                )}>5</span>
                                Instrument(e) {formData.raum_id && formData.instrument_ids?.length === 0 && "👈"}
                            </Label>
                            
                            {selectedLehrer?.instrumente?.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                    {selectedLehrer.instrumente.map((instId, idx) => {
                                        const isSelected = (formData.instrument_ids || []).includes(instId);
                                        return (
                                            <button
                                                key={instId}
                                                type="button"
                                                onClick={() => toggleInstrument(instId)}
                                                className={cn(
                                                    "px-4 py-2.5 rounded-xl text-sm font-medium border-2 transition-all flex items-center gap-1.5",
                                                    isSelected
                                                        ? "bg-violet-600 border-violet-600 text-white"
                                                        : "bg-violet-50 border-violet-200 text-violet-800 hover:border-violet-400 hover:bg-violet-100"
                                                )}
                                            >
                                                {isSelected && <Check className="w-4 h-4" />}
                                                <Music className="w-4 h-4" />
                                                {selectedLehrer.instrumente_names?.[idx] || instrumente.find(i => i.id === instId)?.name}
                                            </button>
                                        );
                                    })}
                                    {/* Instrument hinzufügen Button */}
                                    <button
                                        type="button"
                                        onClick={() => setShowAllInstruments(!showAllInstruments)}
                                        className="px-4 py-2.5 rounded-xl text-sm font-medium border-2 border-dashed border-violet-300 text-violet-500 hover:border-violet-400 hover:bg-violet-50 transition-all flex items-center gap-1.5"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Instrument hinzufügen
                                    </button>
                                </div>
                            ) : formData.lehrperson_id ? (
                                <div className="space-y-2">
                                    <p className="text-sm text-muted-foreground italic">Keine Instrumente für diesen Lehrer hinterlegt</p>
                                    <button
                                        type="button"
                                        onClick={() => setShowAllInstruments(!showAllInstruments)}
                                        className="px-4 py-2.5 rounded-xl text-sm font-medium border-2 border-dashed border-violet-300 text-violet-500 hover:border-violet-400 hover:bg-violet-50 transition-all flex items-center gap-1.5"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Instrument hinzufügen
                                    </button>
                                </div>
                            ) : null}
                            
                            {/* All instruments expanded */}
                            {showAllInstruments && (
                                <div className="mt-3 p-4 bg-violet-50 rounded-xl border border-violet-200">
                                    <p className="text-sm font-medium text-violet-700 mb-3">Instrument hinzufügen:</p>
                                    <div className="flex flex-wrap gap-2 mb-3 max-h-48 overflow-y-auto">
                                        {instrumente.filter(i => !(selectedLehrer?.instrumente || []).includes(i.id)).map(inst => {
                                            const isSelected = (formData.instrument_ids || []).includes(inst.id);
                                            return (
                                                <button
                                                    key={inst.id}
                                                    type="button"
                                                    onClick={() => toggleInstrument(inst.id)}
                                                    className={cn(
                                                        "px-3 py-2 rounded-lg text-sm border transition-all",
                                                        isSelected
                                                            ? "bg-violet-600 border-violet-600 text-white"
                                                            : "bg-white border-violet-200 text-violet-800 hover:border-violet-400"
                                                    )}
                                                >
                                                    {isSelected ? <Check className="w-3 h-3 inline mr-1" /> : <Plus className="w-3 h-3 inline mr-1" />}
                                                    {inst.name}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <button 
                                        type="button"
                                        onClick={() => setShowAllInstruments(false)} 
                                        className="text-sm text-violet-600 hover:text-violet-800 flex items-center gap-1"
                                    >
                                        <ChevronUp className="w-4 h-4" /> Schließen
                                    </button>
                                </div>
                            )}
                            
                            <p className="text-xs text-muted-foreground mt-2">
                                {formData.instrument_ids?.length || 0} Instrument(e) ausgewählt
                            </p>
                        </div>
                        
                        {/* Step 6: Wochentag */}
                        <div className={cn(formData.instrument_ids?.length === 0 && "opacity-40 pointer-events-none")}>
                            <Label className="text-base font-semibold mb-3 flex items-center gap-2">
                                <span className={cn(
                                    "w-6 h-6 rounded-full inline-flex items-center justify-center text-sm",
                                    formData.wochentag ? "bg-blue-600 text-white" : "bg-blue-500 text-white"
                                )}>6</span>
                                Wochentag {formData.instrument_ids?.length > 0 && !formData.wochentag && "👈"}
                            </Label>
                            <div className="grid grid-cols-6 gap-2">
                                {WOCHENTAGE.map(tag => (
                                    <button
                                        key={tag}
                                        type="button"
                                        onClick={() => setFormData({ ...formData, wochentag: tag })}
                                        className={cn(
                                            "py-2.5 px-3 rounded-xl text-sm font-medium border-2 transition-all",
                                            formData.wochentag === tag
                                                ? "bg-blue-600 border-blue-600 text-white"
                                                : "bg-blue-50 border-blue-200 text-blue-800 hover:border-blue-400 hover:bg-blue-100"
                                        )}
                                    >
                                        {tag.slice(0, 2)}
                                    </button>
                                ))}
                            </div>
                        </div>
                        
                        {/* Step 7: Uhrzeit */}
                        <div className={cn(!formData.wochentag && "opacity-40 pointer-events-none")}>
                            <Label className="text-base font-semibold mb-3 flex items-center gap-2">
                                <span className={cn(
                                    "w-6 h-6 rounded-full inline-flex items-center justify-center text-sm",
                                    formData.zeit_von && formData.zeit_bis ? "bg-slate-800 text-white" : "bg-slate-600 text-white"
                                )}>7</span>
                                Uhrzeit {formData.wochentag && (!formData.zeit_von || !formData.zeit_bis) && "👈"}
                            </Label>
                            <div className="flex items-center gap-4">
                                <div className="flex-1">
                                    <Label className="text-xs text-gray-500 mb-1 block">Von</Label>
                                    <Input 
                                        type="time" 
                                        value={formData.zeit_von} 
                                        onChange={(e) => {
                                            const newVon = e.target.value;
                                            setFormData({ 
                                                ...formData, 
                                                zeit_von: newVon,
                                                zeit_bis: formData.zeit_bis || newVon
                                            });
                                        }} 
                                        className="text-center text-lg"
                                    />
                                </div>
                                <span className="text-gray-400 text-lg mt-5">–</span>
                                <div className="flex-1">
                                    <Label className="text-xs text-gray-500 mb-1 block">Bis</Label>
                                    <Input 
                                        type="time" 
                                        value={formData.zeit_bis} 
                                        onChange={(e) => setFormData({ ...formData, zeit_bis: e.target.value })} 
                                        className="text-center text-lg"
                                    />
                                </div>
                            </div>
                        </div>
                        
                        {/* Optional: Bemerkung */}
                        <div className={cn(!formData.zeit_bis && "opacity-40 pointer-events-none")}>
                            <Label className="text-sm font-medium text-gray-600 mb-2 block">Bemerkung (optional)</Label>
                            <Textarea 
                                value={formData.bemerkung} 
                                onChange={(e) => setFormData({ ...formData, bemerkung: e.target.value })} 
                                placeholder="z.B. Anfänger willkommen" 
                                rows={2} 
                                className="resize-none" 
                            />
                        </div>
                        
                        {/* Spezialangebot - am Ende */}
                        <div className={cn("p-4 bg-amber-50 border border-amber-200 rounded-xl", !formData.zeit_bis && "opacity-40 pointer-events-none")}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <Label className="font-medium text-amber-800">⭐ Spezialangebot</Label>
                                    <p className="text-xs text-amber-600">Wird in allen Gemeinden als Spezialangebot angezeigt</p>
                                </div>
                                <Switch
                                    checked={formData.gemeindeuebergreifend}
                                    onCheckedChange={(v) => setFormData({ ...formData, gemeindeuebergreifend: v })}
                                />
                            </div>
                        </div>
                        
                        {/* Spezialangebot Text */}
                        {formData.gemeindeuebergreifend && (
                            <div className="p-4 bg-amber-50/50 border border-amber-100 rounded-xl">
                                <Label className="font-medium text-amber-800 mb-2 block">Spezialangebot-Text (optional)</Label>
                                <Textarea 
                                    value={formData.spezialangebot_text} 
                                    onChange={(e) => setFormData({ ...formData, spezialangebot_text: e.target.value })} 
                                    placeholder="z.B. **Schnuppertag** für alle Altersgruppen!" 
                                    rows={2} 
                                    className="resize-none font-mono text-sm" 
                                />
                                <p className="text-xs text-amber-600 mt-1">Markdown: **fett**, *kursiv*</p>
                            </div>
                        )}
                        
                        {/* Summary */}
                        {isFormValid && (
                            <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                                <p className="text-green-800 text-center">
                                    <strong>{formData.wochentag}</strong>, {formData.zeit_von} – {formData.zeit_bis}
                                    <br />
                                    <span className="text-sm text-green-600">
                                        {selectedLehrer?.nachname} {selectedLehrer?.vorname} • 
                                        {formData.instrument_ids?.map(id => instrumente.find(i => i.id === id)?.name).filter(Boolean).join(', ')} • 
                                        {unterrichtsorte.find(o => o.id === formData.unterrichtsort_id)?.name} ({getRaumName(formData.raum_id)})
                                    </span>
                                </p>
                            </div>
                        )}
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">Abbrechen</Button>
                        <Button onClick={handleSave} disabled={!isFormValid} className="rounded-xl">{editItem ? 'Speichern' : 'Erstellen'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={!!deleteItem} onOpenChange={() => setDeleteItem(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Angebot löschen?</AlertDialogTitle>
                        <AlertDialogDescription>Diese Aktion kann nicht rückgängig gemacht werden.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Abbrechen</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-xl">Löschen</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Import/Export Dialog */}
            <Dialog open={dataDialogOpen} onOpenChange={setDataDialogOpen}>
                <DialogContent className="max-w-xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FolderSync className="h-5 w-5" />
                            Angebote Import / Export
                        </DialogTitle>
                        <DialogDescription>
                            Daten exportieren oder aus Backup wiederherstellen
                        </DialogDescription>
                    </DialogHeader>
                    
                    <Tabs defaultValue="export" className="mt-4">
                        <TabsList className="grid w-full grid-cols-2 mb-4">
                            <TabsTrigger value="export" className="flex items-center gap-2">
                                <FileDown className="h-4 w-4" />
                                Exportieren
                            </TabsTrigger>
                            <TabsTrigger value="import" className="flex items-center gap-2">
                                <FileUp className="h-4 w-4" />
                                Importieren
                            </TabsTrigger>
                        </TabsList>
                        
                        {/* EXPORT TAB */}
                        <TabsContent value="export" className="space-y-4">
                            <div className="grid gap-4">
                                {/* JSON Backup Export */}
                                <div className="p-4 border rounded-xl hover:bg-slate-50 transition-colors">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-start gap-3">
                                            <div className="p-2 bg-emerald-100 rounded-lg">
                                                <Download className="h-5 w-5 text-emerald-600" />
                                            </div>
                                            <div>
                                                <p className="font-semibold">Vollständiges Backup (JSON)</p>
                                                <p className="text-sm text-muted-foreground">
                                                    Alle {items.length} Angebote inkl. IDs für Wiederherstellung
                                                </p>
                                            </div>
                                        </div>
                                        <Button onClick={handleJSONExport} className="rounded-xl">
                                            Herunterladen
                                        </Button>
                                    </div>
                                </div>
                                
                                {/* CSV Export */}
                                <div className="p-4 border rounded-xl hover:bg-slate-50 transition-colors">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-start gap-3">
                                            <div className="p-2 bg-blue-100 rounded-lg">
                                                <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                                            </div>
                                            <div>
                                                <p className="font-semibold">CSV-Export</p>
                                                <p className="text-sm text-muted-foreground">
                                                    Für Excel/Tabellenkalkulation
                                                </p>
                                            </div>
                                        </div>
                                        <Button variant="outline" onClick={handleCSVExport} className="rounded-xl">
                                            Herunterladen
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </TabsContent>
                        
                        {/* IMPORT TAB */}
                        <TabsContent value="import" className="space-y-6">
                            {/* JSON Restore */}
                            <div className="p-4 border rounded-xl">
                                <div className="flex items-start gap-3 mb-3">
                                    <div className="p-2 bg-emerald-100 rounded-lg">
                                        <Upload className="h-5 w-5 text-emerald-600" />
                                    </div>
                                    <div>
                                        <p className="font-semibold">Backup wiederherstellen (JSON)</p>
                                        <p className="text-sm text-muted-foreground">
                                            Aus zuvor exportiertem JSON-Backup. Bestehende Einträge werden aktualisiert.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <input
                                        type="file"
                                        accept=".json"
                                        onChange={(e) => setJsonImportFile(e.target.files[0])}
                                        ref={jsonInputRef}
                                        className="flex-1 text-sm file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-primary file:text-primary-foreground file:cursor-pointer cursor-pointer"
                                    />
                                    <Button 
                                        onClick={handleJSONImport} 
                                        disabled={!jsonImportFile || jsonImporting}
                                        className="rounded-xl"
                                    >
                                        {jsonImporting ? 'Lädt...' : 'Importieren'}
                                    </Button>
                                </div>
                                {jsonImportResult && (
                                    <div className="mt-3">
                                        {(jsonImportResult.imported > 0 || jsonImportResult.updated > 0) ? (
                                            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2 text-emerald-800">
                                                <CheckCircle2 className="h-5 w-5" />
                                                <span className="font-medium">Erfolgreich!</span>
                                                <span className="text-sm">{jsonImportResult.imported} neu, {jsonImportResult.updated} aktualisiert</span>
                                            </div>
                                        ) : jsonImportResult.errors?.length > 0 && (
                                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                                                <AlertCircle className="h-4 w-4 inline mr-2" />
                                                {jsonImportResult.errors.map(e => e.error).join(', ')}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </TabsContent>
                    </Tabs>
                </DialogContent>
            </Dialog>
        </>
    );
}
